/**
 * DEV placeholder textures for 绿色码头 (never ship). Draws the `gh-tiles` tileset and every `gh-*`
 * prop into CanvasTextures at the exact sizes in PROP_SPECS, so real art replaces them by texture
 * key alone. Palette = ART_DIRECTION sunlit-harbor keys; labels are ASCII only (Chinese is never
 * baked into images). Deterministic: speckles come from SeededRng.
 */
import type Phaser from 'phaser';
import { SeededRng } from '@/core/rng';
import { registerTileset } from '@/maps/tilesets';
import { GH_TILESET_COLUMNS, GH_TILESET_KEY, GH_TILE_COUNT, P, PROP_SPECS, T, type PropKey } from './green-harbor';

type Ctx2D = CanvasRenderingContext2D;
const TS = 32;

// ART_DIRECTION palette
const C = {
  sand: '#e2c48c', sandDark: '#c9a86e', sandWet: '#b9955f', teak: '#8a5a32', teakDark: '#5a3a1e', teakLight: '#b07a3c',
  lacquer: '#8c2a22', lacquerDark: '#5a1a14', indigo: '#2b3a5c', indigoDark: '#1a2238', palm: '#3c7a3a', palmLight: '#8ec95a',
  palmDark: '#2a5a2a', water: '#1f8f8a', waterLight: '#6fd3c8', waterDeep: '#0f5a58', storm: '#3a3f57', lantern: '#ffb347',
  stone: '#8c8c86', stoneDark: '#6b6b73', earth: '#8a5a32', earthDark: '#6e4524', parchment: '#f3e6c8', pale: '#e8e2d6',
  rope: '#c9b89a', iron: '#3a3f57', shell: '#6a5a3a', shellLight: '#9a8a5a', shellDark: '#3e3420', chalk: '#efe7d8',
};

function canvasTexture(scene: Phaser.Scene, key: string, w: number, h: number): Phaser.Textures.CanvasTexture | null {
  if (scene.textures.exists(key)) return null;
  const tex = scene.textures.createCanvas(key, w, h);
  if (!tex) { console.warn(`[green-harbor/dev] could not create canvas texture ${key}`); return null; }
  tex.context.imageSmoothingEnabled = false;
  return tex;
}

function label(ctx: Ctx2D, text: string, x: number, y: number, color = '#00000088'): void {
  ctx.font = '7px monospace';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = color;
  ctx.fillText(text, x, y);
}

type R = (x: number, y: number, w: number, h: number, c: string) => void;
const rectFn = (ctx: Ctx2D, ox = 0, oy = 0): R => (x, y, w, h, c) => { ctx.fillStyle = c; ctx.fillRect(ox + x, oy + y, w, h); };

// ------------------------------------------------------------------ tiles
function drawTile(ctx: Ctx2D, id: number, ox: number, oy: number, rng: SeededRng): void {
  const r = rectFn(ctx, ox, oy);
  const speck = (n: number, c: string, w = 1, h = 1) => { for (let i = 0; i < n; i++) r(rng.int(1, TS - 1 - w), rng.int(1, TS - 1 - h), w, h, c); };
  switch (id) {
    case T.WATER: r(0, 0, TS, TS, C.water); for (let y = 5; y < TS; y += 11) { r(2, y, 10, 1, C.waterLight); r(18, y + 5, 9, 1, C.waterLight); } break;
    case T.WATER_DEEP: r(0, 0, TS, TS, C.waterDeep); r(6, 9, 8, 1, C.water); r(20, 22, 8, 1, C.water); break;
    case T.SAND: r(0, 0, TS, TS, C.sand); speck(9, C.sandDark); break;
    case T.EARTH: r(0, 0, TS, TS, C.earth); speck(7, C.earthDark, 2, 1); break;
    case T.STONE: r(0, 0, TS, TS, C.stone); for (let y = 0; y < TS; y += 16) { r(0, y, TS, 1, C.stoneDark); const off = y ? 16 : 0; for (let x = off; x < TS; x += 32) r(x, y, 1, 16, C.stoneDark); } r(0, 16, 1, 16, C.stoneDark); break;
    case T.PLANK: r(0, 0, TS, TS, C.teakLight); for (let y = 0; y < TS; y += 8) r(0, y, TS, 1, C.earthDark); r(15, 0, 1, TS, C.teak); break;
    case T.PLANK_EDGE: r(0, 0, TS, TS, C.teakLight); for (let y = 0; y < TS; y += 8) r(0, y, TS, 1, C.earthDark); r(0, 0, 3, TS, C.teakDark); r(29, 0, 3, TS, C.teakDark); r(12, 4, 8, 6, C.teakDark); break;
    case T.GRASS: r(0, 0, TS, TS, C.palmLight); speck(11, C.palm, 1, 2); break;
    case T.PAVING: r(0, 0, TS, TS, '#d3b57f'); speck(6, C.sandDark, 3, 2); r(0, 15, TS, 1, '#c4a56f'); break;
    case T.SHADOW: r(0, 0, TS, TS, 'rgba(20,16,30,0.28)'); break;
    case T.LEAF: r(0, 0, TS, TS, C.palm); r(2, 2, 28, 28, '#4f9a48'); r(15, 0, 2, TS, C.palmDark); for (let i = 0; i < 6; i++) { r(3, 4 + i * 5, 12, 1, C.palmDark); r(17, 6 + i * 5, 12, 1, C.palmDark); } break;
    case T.LEAF_EDGE: r(0, 0, TS, TS, 'rgba(60,122,58,0.55)'); r(0, 0, 8, TS, C.palm); r(8, 6, 4, 20, '#4f9a48'); break;
    case T.WET_SAND: r(0, 0, TS, TS, C.sandWet); speck(6, C.sandDark); r(4, 20, 12, 1, '#a88650'); break;
    case T.INN_FLOOR: r(0, 0, TS, TS, C.teakDark); for (let y = 0; y < TS; y += 8) r(0, y, TS, 1, '#3a2412'); r(15, 0, 1, TS, '#4a2e18'); break;
    case T.FOAM: r(0, 0, TS, TS, C.water); r(0, 20, TS, 12, C.sand); r(0, 17, TS, 3, C.chalk); r(4, 14, 8, 2, C.waterLight); r(18, 15, 10, 2, C.waterLight); break;
    case T.PALM_SHADOW: r(0, 0, TS, TS, 'rgba(20,16,30,0.18)'); break;
    case T.LEAF_SHADE: r(0, 0, TS, TS, 'rgba(60,122,58,0.22)'); break;
    case T.ROAD_EDGE: r(0, 0, TS, TS, C.palmLight); speck(6, C.palm, 1, 2); r(10, 0, 12, TS, C.earth); break;
    default: r(0, 0, TS, TS, '#ff00ff');
  }
}

/** Draws the `gh-tiles` tileset (order = T) and registers the tileset id. Idempotent. */
export function ensureHarborTileset(scene: Phaser.Scene): string {
  registerTileset({ id: GH_TILESET_KEY, texture: GH_TILESET_KEY, tileWidth: TS, tileHeight: TS });
  const cols = GH_TILESET_COLUMNS, rows = Math.ceil(GH_TILE_COUNT / cols);
  const tex = canvasTexture(scene, GH_TILESET_KEY, cols * TS, rows * TS);
  if (!tex) return GH_TILESET_KEY;
  const ctx = tex.context;
  const rng = new SeededRng('gh-tiles');
  for (let i = 0; i < GH_TILE_COUNT; i++) drawTile(ctx, i, (i % cols) * TS, Math.floor(i / cols) * TS, rng);
  tex.refresh();
  return GH_TILESET_KEY;
}

// ------------------------------------------------------------------ props
function outline(ctx: Ctx2D, x: number, y: number, w: number, h: number, c: string): void {
  ctx.fillStyle = c;
  ctx.fillRect(x, y, w, 1); ctx.fillRect(x, y + h - 1, w, 1); ctx.fillRect(x, y, 1, h); ctx.fillRect(x + w - 1, y, 1, h);
}

/** A box with a lit top face and a darker front face (3/4 view). */
function box(ctx: Ctx2D, x: number, y: number, w: number, h: number, top: number, face: string, topC: string, dark: string): void {
  const r = rectFn(ctx);
  r(x, y, w, top, topC);
  r(x, y + top, w, h - top, face);
  outline(ctx, x, y, w, h, dark);
}

function crate(ctx: Ctx2D, x: number, y: number, w: number, h: number): void {
  box(ctx, x, y, w, h, Math.round(h * 0.3), '#a8743a', '#d9a45a', C.teakDark);
  const r = rectFn(ctx);
  r(x + 2, y + Math.round(h * 0.3) + 2, w - 4, 1, C.teakDark); r(x + 2, y + h - 3, w - 4, 1, C.teakDark);
}

function jar(ctx: Ctx2D, x: number, y: number, w: number, h: number, c = '#b8734a'): void {
  const r = rectFn(ctx);
  r(x + 2, y + 4, w - 4, h - 4, c); r(x, y + 8, w, h - 12, c); r(x + 3, y + 2, w - 6, 3, C.teakDark);
  outline(ctx, x, y + 8, w, h - 12, C.teakDark); r(x + 3, y + 10, 2, h - 16, '#e2b48c');
}

function awningStall(ctx: Ctx2D, w: number, h: number, awning: string, awningDark: string, goods: (r: R) => void, tag: string): void {
  const r = rectFn(ctx);
  // posts
  r(4, 20, 4, h - 24, C.teakDark); r(w - 8, 20, 4, h - 24, C.teakDark);
  // counter (front face + top)
  const cy = h - 30;
  box(ctx, 2, cy, w - 4, 30, 10, C.teak, C.teakLight, C.teakDark);
  goods(r);
  // awning (scalloped)
  r(0, 8, w, 14, awning); r(0, 4, w, 4, awningDark);
  for (let x = 0; x < w; x += 8) r(x + 2, 22, 4, 3, awningDark);
  outline(ctx, 0, 4, w, 18, awningDark);
  label(ctx, tag, 6, h - 4, '#ffffffaa');
}

function drawProp(ctx: Ctx2D, key: PropKey, w: number, h: number, rng: SeededRng): void {
  const r = rectFn(ctx);
  switch (key) {
    case P.FOREIGN_SHIP: {
      // tall pale hull, high prow to the right, one mast — moored beside the pier
      r(8, 120, w - 16, 90, C.pale); r(8, 200, w - 16, 12, C.storm); r(w - 30, 96, 22, 40, C.pale);
      outline(ctx, 8, 120, w - 16, 92, '#9a948a'); r(12, 150, w - 24, 2, C.lacquer);
      r(70, 20, 6, 110, C.teakDark); r(40, 30, 66, 4, C.teakDark); r(44, 34, 58, 44, C.chalk); outline(ctx, 44, 34, 58, 44, '#b8b0a0');
      for (let i = 0; i < 6; i++) r(14 + i * 22, 165, 8, 6, C.indigoDark);
      label(ctx, 'foreign ship', 12, h - 6); break;
    }
    case P.JUNK: {
      // beached junk: tilted brown hull, bare mast, nets hanging
      r(20, 120, w - 60, 60, C.teak); r(w - 50, 100, 30, 80, C.teak); r(20, 170, w - 40, 12, C.teakDark);
      outline(ctx, 20, 120, w - 40, 62, C.teakDark); r(30, 140, w - 70, 2, C.lacquer);
      r(130, 10, 6, 112, C.teakDark); r(96, 24, 72, 3, C.teakDark);
      for (let i = 0; i < 9; i++) r(100 + i * 8, 28, 1, 40 + (i % 3) * 8, C.rope);
      for (let i = 0; i < 8; i++) r(28 + i * 30, 150, 10, 8, C.teakDark);
      label(ctx, 'beached junk', 24, h - 6); break;
    }
    case P.SHED: {
      // warehouse shed: big-leaf roof, open front with dark interior, stacked goods inside
      r(8, 40, w - 16, h - 44, C.teakDark); r(8, 40, w - 16, 6, '#3a2412');
      for (let i = 0; i < 4; i++) crate(ctx, 20 + i * 48, h - 44, 28, 26);
      r(0, 10, w, 34, C.palm); for (let x = 0; x < w; x += 14) r(x, 40, 10, 5, C.palmDark); for (let i = 0; i < 12; i++) r(4 + i * 18, 14, 10, 22, '#4f9a48');
      r(0, 8, w, 3, C.palmDark); r(4, 44, 4, h - 48, C.teakDark); r(w - 8, 44, 4, h - 48, C.teakDark);
      label(ctx, 'shed', 6, h - 6, '#ffffffaa'); break;
    }
    case P.CRANE: {
      // wooden crane: A-frame, winch drum, rope, hanging crate
      r(14, 20, 6, h - 24, C.teakDark); r(44, 20, 6, h - 24, C.teakDark); r(14, 20, 36, 5, C.teakDark); r(10, h - 10, 44, 8, C.teak);
      r(48, 6, 12, 14, C.teakDark); r(52, 20, 2, 50, C.rope); crate(ctx, 42, 70, 22, 20);
      r(22, 60, 20, 10, C.teak); r(24, 62, 16, 6, C.rope);
      label(ctx, 'crane', 4, h - 14, '#ffffffaa'); break;
    }
    case P.CRATES: crate(ctx, 2, 32, 28, 36); crate(ctx, 32, 32, 30, 36); crate(ctx, 16, 6, 30, 30); label(ctx, 'crates', 4, h - 4, '#ffffffaa'); break;
    case P.BARRELS: r(4, 4, 24, 34, C.teakLight); outline(ctx, 4, 4, 24, 34, C.teakDark); r(4, 12, 24, 2, C.iron); r(4, 28, 24, 2, C.iron); r(8, 6, 16, 3, C.teakDark); break;
    case P.JARS: jar(ctx, 2, 4, 20, 38); jar(ctx, 22, 8, 18, 34, '#a8643a'); jar(ctx, 42, 2, 20, 40, '#c8845a'); break;
    case P.ROPE: r(4, 8, 24, 14, C.rope); r(8, 11, 16, 8, C.teakLight); r(12, 13, 8, 4, C.rope); outline(ctx, 4, 8, 24, 14, C.teak); break;
    case P.NET: {
      r(4, 4, 4, h - 6, C.teakDark); r(56, 4, 4, h - 6, C.teakDark); r(4, 4, 56, 3, C.teakDark);
      for (let y = 10; y < 46; y += 6) r(8, y, 48, 1, C.rope); for (let x = 8; x < 56; x += 6) r(x, 10, 1, 36, C.rope);
      for (let i = 0; i < 5; i++) r(12 + i * 9, 20 + (i % 2) * 12, 3, 5, C.palm);
      label(ctx, 'net', 22, h - 4); break;
    }
    case P.STALL_CANDLE: awningStall(ctx, w, h, C.lacquer, C.lacquerDark, (rr) => { for (let i = 0; i < 6; i++) rr(12 + i * 13, h - 40, 4, 12, C.chalk); for (let i = 0; i < 6; i++) rr(13 + i * 13, h - 42, 2, 2, C.lantern); rr(8, h - 28, w - 16, 3, C.indigo); }, 'candles'); break;
    case P.STALL_SPICE: awningStall(ctx, w, h, C.lantern, '#c47a1e', (rr) => { const cs = ['#c85a1e', '#e2a12a', '#8c2a22', '#b8734a', '#d9c25a']; for (let i = 0; i < 5; i++) { rr(8 + i * 17, h - 40, 14, 12, C.rope); rr(10 + i * 17, h - 44, 10, 6, cs[i] ?? C.lacquer); } }, 'spices'); break;
    case P.STALL_CLOTH: awningStall(ctx, w, h, C.indigo, C.indigoDark, (rr) => { const cs = [C.indigo, '#6b6b73', C.parchment, '#4a5a3a']; for (let i = 0; i < 4; i++) rr(8 + i * 21, h - 42, 18, 14, cs[i] ?? C.indigo); rr(8, h - 30, w - 16, 2, C.teakDark); }, 'cloth'); break;
    case P.STALL_FRUIT: awningStall(ctx, w, h, C.palmLight, C.palm, (rr) => { const cs = ['#c8402a', '#7a3a8a', '#e2a12a', '#8ec95a', '#c8402a', '#d97a2a']; for (let i = 0; i < 6; i++) { rr(9 + i * 14, h - 40, 10, 10, cs[i] ?? C.lacquer); rr(11 + i * 14, h - 42, 3, 3, C.palm); } }, 'fruit'); break;
    case P.STALL_FISH: awningStall(ctx, w, h, C.waterLight, C.water, (rr) => { rr(8, h - 44, w - 16, 16, C.teakLight); rr(10, h - 42, w - 20, 12, C.water); for (let i = 0; i < 5; i++) rr(14 + i * 15, h - 38, 10, 4, i % 2 ? '#e2a12a' : '#c8402a'); }, 'fish'); break;
    case P.STALL_JARS: awningStall(ctx, w, h, C.waterLight, C.water, (rr) => { for (let i = 0; i < 4; i++) jar(ctx, 8 + i * 30, h - 60, 26, 34, i % 2 ? '#a8643a' : '#c8845a'); rr(0, 0, 0, 0, C.rope); }, 'big jars'); break;
    case P.STALL_YABEI: awningStall(ctx, w, h, '#2f8f7a', '#1f5f52', (rr) => { rr(8, h - 40, 30, 12, C.rope); rr(12, h - 44, 22, 6, '#c85a1e'); rr(48, h - 42, 20, 14, '#b8734a'); rr(78, h - 44, 40, 16, C.rope); rr(86, h - 42, 24, 10, C.chalk); }, 'yabei stall'); break;
    case P.CACTUS: r(8, 20, 16, 10, '#b8734a'); outline(ctx, 8, 20, 16, 10, C.teakDark); r(10, 6, 12, 14, C.palm); r(12, 4, 8, 4, C.palm); for (let i = 0; i < 6; i++) r(9 + (i % 3) * 5, 7 + Math.floor(i / 3) * 6, 1, 1, C.chalk); break;
    case P.PELT: r(6, 4, 20, 40, '#8a5a3a'); r(8, 8, 16, 32, '#a87050'); r(10, 2, 12, 4, C.rope); outline(ctx, 6, 4, 20, 40, C.teakDark); r(12, 20, 8, 12, '#c8a080'); break;
    case P.EGG: r(10, 4, 12, 20, C.chalk); r(8, 8, 16, 14, C.chalk); r(12, 2, 8, 3, C.chalk); outline(ctx, 8, 8, 16, 14, '#c9b89a'); r(6, 22, 20, 5, C.rope); break;
    case P.PALM: {
      r(28, 40, 8, 70, C.teak); r(30, 40, 3, 70, C.teakLight); r(26, 104, 12, 6, C.teakDark);
      for (const [dx, dy, lw, lh] of [[-26, 24, 28, 8], [30, 20, 30, 8], [-20, 8, 24, 10], [26, 4, 26, 10], [-6, 0, 16, 18], [6, 2, 14, 18]]) {
        r(32 + (dx as number), (dy as number), lw as number, lh as number, C.palm); r(32 + (dx as number) + 2, (dy as number) + 2, (lw as number) - 4, 3, C.palmLight);
      }
      r(28, 30, 8, 10, C.palmDark);
      break;
    }
    case P.LANTERN: r(14, 14, 4, 46, C.teakDark); r(10, 58, 12, 4, C.teakDark); r(8, 2, 16, 18, C.lacquer); r(10, 4, 12, 14, C.lantern); outline(ctx, 8, 2, 16, 18, C.lacquerDark); r(12, 0, 8, 2, C.teakDark); break;
    case P.HUT: {
      // stilted hut: legs, plank floor, wall, thatch roof
      for (const x of [12, 40, 84, 112]) r(x, 90, 6, 46, C.teakDark);
      r(6, 84, w - 12, 8, C.teak); r(14, 40, w - 28, 44, C.teakLight); outline(ctx, 14, 40, w - 28, 44, C.teakDark);
      r(52, 56, 24, 28, C.indigoDark); r(0, 8, w, 34, C.palm); r(0, 4, w, 5, C.palmDark); for (let i = 0; i < 9; i++) r(4 + i * 14, 12, 10, 26, '#4f9a48');
      for (let x = 0; x < w; x += 12) r(x, 40, 8, 4, C.palmDark);
      label(ctx, 'stilt hut', 18, h - 4, '#ffffffaa'); break;
    }
    case P.INN: {
      // 大海龟旅店: giant shell roof (hexagonal plates), no walls, dark interior between columns
      const roofH = 250;
      r(24, roofH, w - 48, h - roofH - 24, C.indigoDark);                          // interior seen under the shell
      r(24, h - 30, w - 48, 6, C.teakDark);
      for (let i = 0; i < 6; i++) { r(44 + i * 56, roofH + 20, 6, h - roofH - 46, C.teak); r(50 + i * 56, roofH + 34, 22, 6, C.lantern); }
      // dome
      r(40, 60, w - 80, roofH - 60, C.shell); r(80, 24, w - 160, 40, C.shell); r(120, 8, w - 240, 18, C.shell);
      r(20, 120, w - 40, roofH - 120, C.shell); r(8, 180, w - 16, roofH - 180, C.shell);
      // plates
      for (let y = 30; y < roofH - 10; y += 40) for (let x = 20 + ((y / 40) % 2) * 26; x < w - 40; x += 52) { r(x, y, 46, 34, C.shellLight); outline(ctx, x, y, 46, 34, C.shellDark); r(x + 4, y + 4, 12, 3, C.chalk); }
      r(8, roofH - 6, w - 16, 8, C.shellDark); r(8, roofH + 2, w - 16, 4, C.shellLight);
      label(ctx, 'turtle inn', 30, h - 8, '#ffffffaa'); break;
    }
    case P.TURTLE: r(6, 6, 20, 12, C.shell); r(8, 4, 16, 4, C.shellLight); r(2, 10, 6, 6, C.shellLight); r(24, 12, 6, 5, C.shellLight); outline(ctx, 6, 6, 20, 12, C.shellDark); for (let i = 0; i < 3; i++) r(9 + i * 6, 9, 4, 5, C.shellLight); r(4, 18, 24, 3, C.chalk); break;
    case P.BOLLARD: r(10, 8, 12, 22, C.teakDark); r(8, 6, 16, 5, C.teak); r(12, 14, 8, 4, C.rope); r(6, 28, 20, 3, '#5a3a1e'); break;
    case P.SIGN_INN: r(14, 14, 4, 32, C.teakDark); r(4, 2, 24, 16, C.lacquer); outline(ctx, 4, 2, 24, 16, C.lacquerDark); r(8, 6, 16, 2, C.lantern); r(8, 11, 16, 2, C.lantern); r(12, 44, 8, 4, C.teakDark); break;
    case P.COLUMN: r(10, 0, 12, h - 6, C.teak); r(12, 0, 3, h - 6, C.teakLight); r(8, h - 8, 16, 8, C.teakDark); r(9, 30, 14, 6, C.rope); r(9, 40, 14, 3, C.rope); r(22, 20, 3, 3, C.iron); break;
    default: r(0, 0, w, h, '#ff00ff'); label(ctx, key, 2, h - 4);
  }
  if (rng.next() < 0) label(ctx, '', 0, 0); // keep rng in the signature for future speckle work (deterministic no-op)
}

/** Every `gh-*` prop texture at its PROP_SPECS size (idempotent). */
export function ensureHarborProps(scene: Phaser.Scene): void {
  const rng = new SeededRng('gh-props');
  for (const key of Object.values(P)) {
    const spec = PROP_SPECS[key];
    const tex = canvasTexture(scene, key, spec.w, spec.h);
    if (!tex) continue;
    drawProp(tex.context, key, spec.w, spec.h, rng);
    tex.refresh();
  }
}

/** Copies one frame of a loaded dev portrait atlas into a plain texture under the real portrait key. */
export function ensurePortraitFromDevAtlas(scene: Phaser.Scene, devKey: string, targetKey: string, frame = 'neutral-0'): boolean {
  if (scene.textures.exists(targetKey)) return true;
  if (!scene.textures.exists(devKey)) return false;
  const src = scene.textures.get(devKey);
  const f = src.has(frame) ? src.get(frame) : src.get();
  const tex = canvasTexture(scene, targetKey, f.width, f.height);
  if (!tex) return false;
  const image = src.getSourceImage() as CanvasImageSource;
  tex.context.drawImage(image, f.cutX, f.cutY, f.cutWidth, f.cutHeight, 0, 0, f.width, f.height);
  tex.refresh();
  return true;
}

/** Everything the harbor scene needs before the map runtime builds. */
export function ensureHarborDevTextures(scene: Phaser.Scene): void {
  ensureHarborTileset(scene);
  ensureHarborProps(scene);
}
